import { useEffect, useMemo, useState } from "react";

const teal = "#0d9488";

function accountCapacity(account) {
  const used = Number(account?.NumberOfPonds);
  const licensed = Number(account?.LicensedPondCount);
  return {
    used: Number.isFinite(used) ? used : null,
    licensed: Number.isFinite(licensed) ? licensed : null,
    isFull:
      Number.isFinite(used) &&
      Number.isFinite(licensed) &&
      used >= licensed,
  };
}

function CapacityBadge({ account }) {
  const { used, licensed, isFull } = accountCapacity(account);
  if (used == null && licensed == null) return null;

  const label =
    used != null && licensed != null
      ? `${used} / ${licensed} ponds`
      : used != null
        ? `${used} ponds`
        : `${licensed} licensed`;

  return (
    <span
      className="badge rounded-pill border px-2 py-1 fw-normal"
      style={{
        fontSize: "11px",
        backgroundColor: isFull ? "#fef2f2" : "#f8fafc",
        color: isFull ? "#b91c1c" : "#64748b",
        borderColor: isFull ? "#fecaca" : "#e2e8f0",
      }}
    >
      {isFull ? "Full · " : ""}
      {label}
    </span>
  );
}

/**
 * Super Admin dialog to pick a destination fisher account and move a pond.
 */
const MovePondModal = ({
  pond,
  accounts,
  loadingAccounts,
  submitting,
  onClose,
  onConfirm,
}) => {
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [step, setStep] = useState("pick"); // pick | confirm

  useEffect(() => {
    setQuery("");
    setSelectedId(null);
    setStep("pick");
  }, [pond?.id]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter((a) => {
      const name = String(a.FullName || "").toLowerCase();
      const email = String(a.Email || "").toLowerCase();
      return name.includes(q) || email.includes(q) || String(a.id).includes(q);
    });
  }, [accounts, query]);

  const selected = useMemo(
    () => accounts.find((a) => Number(a.id) === Number(selectedId)) || null,
    [accounts, selectedId]
  );

  if (!pond) return null;

  const closeIfIdle = () => {
    if (!submitting) onClose();
  };

  const goConfirm = () => {
    if (!selected) return;
    setStep("confirm");
  };

  const submitMove = () => {
    if (!selected || submitting) return;
    onConfirm(selected);
  };

  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      style={{ zIndex: 1060, backgroundColor: "rgba(15, 23, 42, 0.45)" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="movePondModalTitle"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) closeIfIdle();
      }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div
          className="modal-content border-0 shadow"
          style={{ borderRadius: "16px", overflow: "hidden" }}
        >
          <div className="modal-header border-0 pb-0 align-items-start px-4 pt-4">
            <div className="me-3 flex-grow-1">
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-3 mb-3"
                style={{
                  width: 44,
                  height: 44,
                  background: "linear-gradient(135deg, #ecfdf5 0%, #ccfbf1 100%)",
                }}
                aria-hidden
              >
                <i className="fa fa-exchange" style={{ color: teal }} />
              </div>
              <h5
                id="movePondModalTitle"
                className="modal-title fw-semibold text-dark mb-1"
              >
                {step === "confirm" ? "Confirm pond move" : "Move pond"}
              </h5>
              <p className="text-muted small mb-0">
                {step === "confirm"
                  ? "Review the destination, then confirm."
                  : "Choose which customer account should own this pond."}
              </p>
            </div>
            <button
              type="button"
              className="btn btn-light border rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
              style={{ width: 36, height: 36 }}
              aria-label="Close"
              disabled={submitting}
              onClick={closeIfIdle}
            >
              <i className="fa fa-times text-secondary" aria-hidden />
            </button>
          </div>

          <div className="modal-body px-4 pt-3 pb-2">
            <div
              className="rounded-3 px-3 py-3 mb-3"
              style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}
            >
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: "0.04em" }}>
                    Pond
                  </div>
                  <div className="fw-semibold text-dark text-truncate">
                    {pond.Name || "Untitled pond"}
                  </div>
                  {pond.Location ? (
                    <div className="text-muted small text-truncate">{pond.Location}</div>
                  ) : null}
                </div>
                <div className="col-md-6">
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: "0.04em" }}>
                    Current account
                  </div>
                  <div className="fw-semibold text-dark text-truncate">
                    {pond.FisherName || `Account #${pond.FisherID}`}
                  </div>
                </div>
              </div>
            </div>

            {step === "pick" ? (
              <>
                <div className="position-relative mb-3">
                  <div
                    className="position-absolute top-50 translate-middle-y text-muted ps-3"
                    style={{ zIndex: 2 }}
                  >
                    <i className="fa fa-search opacity-50" />
                  </div>
                  <input
                    type="search"
                    className="form-control ps-5"
                    style={{ borderRadius: 12, borderColor: "#e2e8f0" }}
                    placeholder="Search by name or email…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    autoFocus
                    disabled={loadingAccounts || submitting}
                  />
                </div>

                <div
                  className="rounded-3 border overflow-auto"
                  style={{
                    borderColor: "#e2e8f0",
                    maxHeight: "min(340px, 42vh)",
                    minHeight: 180,
                  }}
                  role="listbox"
                  aria-label="Destination accounts"
                >
                  {loadingAccounts ? (
                    <div className="text-center text-muted py-5 small">
                      Loading accounts…
                    </div>
                  ) : filtered.length === 0 ? (
                    <div className="text-center text-muted py-5 small px-3">
                      {accounts.length === 0
                        ? "No other active accounts available."
                        : "No accounts match your search."}
                    </div>
                  ) : (
                    filtered.map((account) => {
                      const id = Number(account.id);
                      const isSelected = Number(selectedId) === id;
                      const { isFull } = accountCapacity(account);
                      return (
                        <button
                          key={id}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          className="w-100 text-start border-0 border-bottom px-3 py-3 d-flex align-items-center gap-3"
                          style={{
                            background: isSelected ? "#f0fdfa" : "#fff",
                            borderColor: "#f1f5f9",
                            boxShadow: isSelected
                              ? `inset 3px 0 0 ${teal}`
                              : "none",
                            cursor: "pointer",
                          }}
                          onClick={() => setSelectedId(id)}
                          disabled={submitting}
                        >
                          <span
                            className="rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0"
                            style={{
                              width: 18,
                              height: 18,
                              border: `2px solid ${isSelected ? teal : "#cbd5e1"}`,
                              background: isSelected ? teal : "transparent",
                            }}
                            aria-hidden
                          >
                            {isSelected ? (
                              <i
                                className="fa fa-check text-white"
                                style={{ fontSize: 9 }}
                              />
                            ) : null}
                          </span>
                          <span className="flex-grow-1 min-w-0">
                            <span className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                              <span className="fw-semibold text-dark text-truncate">
                                {account.FullName || `Account #${id}`}
                              </span>
                              <CapacityBadge account={account} />
                            </span>
                            {account.Email ? (
                              <span
                                className="d-block text-muted text-truncate mt-1"
                                style={{ fontSize: 12 }}
                              >
                                {account.Email}
                              </span>
                            ) : null}
                            {isFull ? (
                              <span
                                className="d-block text-danger mt-1"
                                style={{ fontSize: 11 }}
                              >
                                At licensed limit — increase slots before moving here.
                              </span>
                            ) : null}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>

                <div
                  className="d-flex align-items-start gap-2 mt-3 px-3 py-2 rounded-3"
                  style={{ background: "#fffbeb", border: "1px solid #fde68a" }}
                >
                  <i
                    className="fa fa-exclamation-triangle mt-1 flex-shrink-0"
                    style={{ color: "#d97706", fontSize: 13 }}
                    aria-hidden
                  />
                  <p className="small mb-0" style={{ color: "#92400e" }}>
                    Sampling visits, fish records, stocking, feed, fertilization,
                    and notes move with the pond. Staff assignments on this pond
                    are cleared.
                  </p>
                </div>
              </>
            ) : (
              <div
                className="rounded-3 px-3 py-3"
                style={{ background: "#f0fdfa", border: `1px solid #99f6e4` }}
              >
                <div className="small text-muted mb-2">Moving to</div>
                <div className="fw-semibold text-dark fs-5 mb-1">
                  {selected?.FullName || `Account #${selectedId}`}
                </div>
                {selected?.Email ? (
                  <div className="text-muted small mb-3">{selected.Email}</div>
                ) : (
                  <div className="mb-3" />
                )}
                <p className="small mb-0 text-dark">
                  <strong>{pond.Name || "This pond"}</strong> and all related
                  data will be reassigned from{" "}
                  <strong>{pond.FisherName || "the current account"}</strong> to
                  this account. Staff assignments on the pond will be cleared.
                </p>
              </div>
            )}
          </div>

          <div className="modal-footer border-0 px-4 pb-4 pt-3 gap-2 justify-content-end">
            {step === "confirm" ? (
              <>
                <button
                  type="button"
                  className="btn btn-light border rounded-pill px-3"
                  disabled={submitting}
                  onClick={() => setStep("pick")}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="btn rounded-pill px-4 text-white"
                  style={{ backgroundColor: teal, borderColor: teal }}
                  disabled={submitting || !selected}
                  onClick={submitMove}
                >
                  {submitting ? "Moving…" : "Yes, move pond"}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-light border rounded-pill px-3"
                  disabled={submitting}
                  onClick={closeIfIdle}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn rounded-pill px-4 text-white"
                  style={{ backgroundColor: teal, borderColor: teal }}
                  disabled={
                    submitting ||
                    !selected ||
                    accountCapacity(selected).isFull
                  }
                  onClick={goConfirm}
                >
                  Continue
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MovePondModal;
